// utilies/geojson/GeneratePoints.js
import * as Cesium from "cesium";

/**
 * Densify polylines/polygons of a Cesium DataSource.
 * New points are inserted into positions list of each entity.
 * Height effect is removed by projecting to height = 0.
 */
export function generatePointsCesiumDataSource(dataSource, spacingMeters = 5) {
  if (!dataSource || !dataSource.entities || spacingMeters <= 0) return;

  const entities = dataSource.entities.values;

  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];

    // polyline
    if (e.polyline && e.polyline.positions) {
      const positions = getPositionsValue(e.polyline.positions);
      if (positions && positions.length > 1) {
        const densified = densifyPositions(positions, spacingMeters, false);
        // set back on entity
        e.polyline.positions = new Cesium.ConstantProperty(densified);
      }
    }

    // polygon (border)
    if (e.polygon && e.polygon.hierarchy) {
      const hierarchy = getHierarchyValue(e.polygon.hierarchy);
      const positions = hierarchy?.positions;
      if (positions && positions.length > 1) {
        const densified = densifyPositions(positions, spacingMeters, true);
        const newHierarchy = new Cesium.PolygonHierarchy(densified);

        // if hierarchy is a Property
        if (typeof e.polygon.hierarchy.setValue === "function") {
          e.polygon.hierarchy.setValue(newHierarchy);
        } else {
          e.polygon.hierarchy = new Cesium.ConstantProperty(newHierarchy);
        }
      }
    }
  }
}

// read positions from Cesium Property or array
function getPositionsValue(prop) {
  if (!prop) return null;
  if (typeof prop.getValue === "function") {
    return prop.getValue(Cesium.JulianDate.now()); // get current positions
  }
  return prop;
}

// read hierarchy from Cesium Property or object
function getHierarchyValue(prop) {
  if (!prop) return null;
  if (typeof prop.getValue === "function") {
    return prop.getValue(Cesium.JulianDate.now());
  }
  return prop;
}

/**
 * Return new positions array with extra points between vertices.
 */
function densifyPositions(positions, spacingMeters, closeRing) {
  if (!positions || positions.length < 2 || spacingMeters <= 0) return positions;

  const result = [];
  const n = positions.length;
  const count = closeRing ? n : n - 1;

  for (let i = 0; i < count; i++) {
    const a = positions[i];
    const b = positions[(i + 1) % n];

    // keep original vertex
    result.push(a);

    const segLen = Cesium.Cartesian3.distance(a, b);
    if (segLen <= 0) continue;

    let d = spacingMeters;
    while (d < segLen) {
      const t = d / segLen;

      // interpolate on segment
      const temp = new Cesium.Cartesian3();
      Cesium.Cartesian3.lerp(a, b, t, temp);

      // project to surface (ignore height)
      const carto = Cesium.Cartographic.fromCartesian(temp);
      carto.height = 0;
      const p = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, 0);

      result.push(p);
      d += spacingMeters;
    }
  }

  if (!closeRing) {
    result.push(positions[n - 1]); // last vertex
  }

  return result;
}
